import { Tab } from '@mui/material';
import { AxiosError } from 'axios';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from 'react-query';

import { telegramFlowApi, telegramUsersApi } from 'api';
import {
  QueryTabs,
  QueryTabPanel,
  PageHeader,
  CloseFormikDialogResult,
} from 'components';
import { QueryKey } from 'enums';
import { useQueryTab, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { validationUtils } from 'utils';

import { ProvidersTab } from './providers';
import { ShopsTab } from './shops';
import { TelegramFlowDialog, TelegramFlowContext } from './TelegramFlowDialog';
import { TradersTab } from './traders';
import { UsersTab, TelegramUserDetailsDialog } from './users';

export enum TelegramFlowTabs {
  Shops = 'shops',
  Traders = 'traders',
  Providers = 'providers',
  Users = 'users',
}

export const TelegramFlow: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow',
  });
  const { tab } = useQueryTab<TelegramFlowTabs>();
  const queryClient = useQueryClient();

  const { data: telegramUsers = [] } = useQuery(
    QueryKey.TelegramUsers,
    telegramUsersApi.getAll,
  );

  const [dialog, setDialog] = useState<{
    open: boolean;
    context: TelegramFlowContext | null;
  }>({ open: false, context: null });

  const [createUserDialog, setCreateUserDialog] = useState<{
    open: boolean;
  }>({ open: false });

  const { mutate: createTelegramFlow } = useMutation(telegramFlowApi.create);

  const createUserMutation = useMutation(telegramUsersApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.TelegramUsers);
      setCreateUserDialog({ open: false });
    },
    notifierType: 'save',
  });

  const getInvalidateKey = useCallback(() => {
    if (tab === TelegramFlowTabs.Traders) {
      return QueryKey.TelegramFlowTraders;
    }
    if (tab === TelegramFlowTabs.Shops) {
      return QueryKey.TelegramFlowShops;
    }
    if (tab === TelegramFlowTabs.Providers) {
      return QueryKey.TelegramFlowProviders;
    }
    if (tab === TelegramFlowTabs.Users) {
      return QueryKey.TelegramUsers;
    }
    return undefined;
  }, [tab]);

  const handleCreateClick = useCallback(() => {
    if (tab === TelegramFlowTabs.Users) {
      setCreateUserDialog({ open: true });
      return;
    }

    let context: TelegramFlowContext | null = null;
    if (tab === TelegramFlowTabs.Traders) {
      context = TelegramFlowContext.Trader;
    } else if (tab === TelegramFlowTabs.Shops) {
      context = TelegramFlowContext.Shop;
    } else if (tab === TelegramFlowTabs.Providers) {
      context = TelegramFlowContext.P2PProvider;
    }
    setDialog({ open: true, context });
  }, [tab]);

  const handleDialogClose = useCallback(
    (result: CloseFormikDialogResult<any>) => {
      if (result?.ok && result?.data) {
        const { values, formikHelpers } = result.data;
        createTelegramFlow(values, {
          onSuccess: () => {
            const key = getInvalidateKey();
            if (key) {
              queryClient.invalidateQueries(key);
            }
            formikHelpers.resetForm();
            setDialog({ open: false, context: null });
          },
          onError: (error: AxiosError) => {
            formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        });
      } else {
        setDialog({ open: false, context: null });
      }
    },
    [createTelegramFlow, getInvalidateKey, queryClient],
  );

  const handleCreateUserDialogClose = useCallback(
    (result: CloseFormikDialogResult<{ name: string; phone: string }>) => {
      if (result.ok && result.data) {
        const { values, formikHelpers } = result.data;
        createUserMutation.mutate(values, {
          onSuccess: () => {
            formikHelpers.resetForm();
            setCreateUserDialog({ open: false });
          },
          onError: (error: AxiosError) => {
            formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        });
      } else {
        result.data?.formikHelpers?.resetForm();
        setCreateUserDialog({ open: false });
      }
    },
    [createUserMutation],
  );

  return (
    <div>
      <PageHeader
        title={t('title')}
        divider={false}
        rightContentButton={{ onClick: handleCreateClick }}
      />
      <QueryTabs tabsEnum={TelegramFlowTabs}>
        <Tab value={TelegramFlowTabs.Shops} label={t('tabs.shops')} />
        <Tab value={TelegramFlowTabs.Traders} label={t('tabs.traders')} />
        <Tab value={TelegramFlowTabs.Providers} label={t('tabs.providers')} />
        <Tab value={TelegramFlowTabs.Users} label={t('tabs.users')} />
      </QueryTabs>
      <QueryTabPanel value={TelegramFlowTabs.Shops}>
        <ShopsTab telegramUsers={telegramUsers} />
      </QueryTabPanel>
      <QueryTabPanel value={TelegramFlowTabs.Traders}>
        <TradersTab telegramUsers={telegramUsers} />
      </QueryTabPanel>
      <QueryTabPanel value={TelegramFlowTabs.Providers}>
        <ProvidersTab telegramUsers={telegramUsers} />
      </QueryTabPanel>
      <QueryTabPanel value={TelegramFlowTabs.Users}>
        <UsersTab />
      </QueryTabPanel>
      <TelegramFlowDialog
        open={dialog.open}
        onClose={handleDialogClose}
        context={dialog.context || TelegramFlowContext.Trader}
        telegramUsers={telegramUsers}
      />
      <TelegramUserDetailsDialog
        open={createUserDialog.open}
        onClose={handleCreateUserDialogClose}
        mutation={createUserMutation}
      />
    </div>
  );
};
